import { HttpClient, HttpHeaders } from '@angular/common/http';
import type { HttpParams } from '@angular/common/http';
import { Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { CbreHttpConfig, CbreHttpConfigToken } from '../cbre-http.config';
import { X_CBRE_HTTP_TRANSACTION_ID_HEADER } from '../utils/constants';

/**
 * Creates a trace id and bind it to each http call performed with the instance.
 * every request made with this client will attach same transaction id but every
 * request will have different request id (binded by Span Interceptor Service)
 */
export class HttpTransactionalClient {
  private readonly headers: HttpHeaders;
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    transactionId: string,
    @Inject(CbreHttpConfigToken) options?: CbreHttpConfig
  ) {
    const { transactionIdHeaderName: headerName = X_CBRE_HTTP_TRANSACTION_ID_HEADER } = options;
    this.baseUrl = options.baseUrl;
    this.headers = new HttpHeaders({
      [headerName]: transactionId,
    });
  }

  /**
   * GET request
   */
  public httpGet(
    uri: string,
    options?: { params?: HttpParams; headers: HttpHeaders; [key: string]: any }
  ): Observable<any> {
    const headers = this.bindTracingHeader(options || {});
    const requestOptions = Object.assign(
      // curr options
      options ? { params: options.params || null } : {},
      // span-ing/tracing headers
      { headers }
    );
    return this.http.get(this.baseUrl + uri, requestOptions).pipe(take(1));
  }

  /**
   * POST request
   */
  public httpPost<T>(uri: string, data: T, options?: any): Observable<any> {
    options = options || {};
    const headers = this.bindTracingHeader(options);
    return this.http.post(this.baseUrl + uri, data, { ...options, headers }).pipe(take(1));
  }

  /**
   * PUT request
   */
  public httpPut<T>(uri: string, data?: T, options?: any): Observable<any> {
    options = options || {};
    const headers = this.bindTracingHeader(options);
    return this.http.put(this.baseUrl + uri, data, { ...options, headers }).pipe(take(1));
  }

  /**
   * DELETE request
   */
  public httpDelete(uri: string, options?: any): Observable<any> {
    options = options || {};
    const headers = this.bindTracingHeader(options);
    return this.http.delete(this.baseUrl + uri, { ...options, headers }).pipe(take(1));
  }

  /**
   * Binds the trace id / spanner header to given http options
   * @param options
   */
  private bindTracingHeader(options: any): HttpHeaders {
    return options && options.headers ? { ...options.headers, ...this.headers } : this.headers;
  }
}
